import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface EducationFormProps {
  initialData?: {
    id?: string;
    institution: string;
    degree: string;
    field_of_study: string;
    start_date: string;
    end_date?: string | null;
  };
  onSave: (data: {
    institution: string;
    degree: string;
    field_of_study: string;
    start_date: string;
    end_date?: string | null;
  }) => void;
  onCancel: () => void;
}

const EducationForm = ({ initialData, onSave, onCancel }: EducationFormProps) => {
  const { toast } = useToast();
  const getYearFromDate = (dateString?: string | null) => {
    if (!dateString) return "";
    return new Date(dateString).getFullYear().toString();
  };

  const [formData, setFormData] = useState({
    institution: initialData?.institution || "",
    degree: initialData?.degree || "",
    field_of_study: initialData?.field_of_study || "",
    start_year: getYearFromDate(initialData?.start_date) || "",
    end_year: getYearFromDate(initialData?.end_date) || "",
  });

  const [errors, setErrors] = useState<{
    institution?: string;
    degree?: string;
    field_of_study?: string;
    start_year?: string;
  }>({});

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!formData.institution.trim()) {
      newErrors.institution = "Institution is required";
    }
    if (!formData.degree.trim()) {
      newErrors.degree = "Degree is required";
    }
    if (!formData.field_of_study.trim()) {
      newErrors.field_of_study = "Field of study is required";
    }
    if (!formData.start_year.trim()) {
      newErrors.start_year = "Start year is required";
    }

    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      toast({
        title: "Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Convert years to ISO date strings (set to January 1st of the year)
    const start_date = `${formData.start_year}-01-01`;
    const end_date = formData.end_year ? `${formData.end_year}-01-01` : null;

    onSave({
      institution: formData.institution,
      degree: formData.degree,
      field_of_study: formData.field_of_study,
      start_date,
      end_date,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="institution">
          Institution <span className="text-red-500">*</span>
        </Label>
        <Input
          id="institution"
          value={formData.institution}
          onChange={(e) => {
            setFormData({ ...formData, institution: e.target.value });
            setErrors(prev => ({ ...prev, institution: undefined }));
          }}
          className={cn(errors.institution && "border-red-500")}
        />
        {errors.institution && (
          <p className="text-sm text-red-500 mt-1">{errors.institution}</p>
        )}
      </div>

      <div>
        <Label htmlFor="degree">
          Degree <span className="text-red-500">*</span>
        </Label>
        <Input
          id="degree"
          value={formData.degree}
          onChange={(e) => {
            setFormData({ ...formData, degree: e.target.value });
            setErrors(prev => ({ ...prev, degree: undefined }));
          }}
          className={cn(errors.degree && "border-red-500")}
        />
        {errors.degree && (
          <p className="text-sm text-red-500 mt-1">{errors.degree}</p>
        )}
      </div>

      <div>
        <Label htmlFor="field_of_study">
          Field of Study <span className="text-red-500">*</span>
        </Label>
        <Input
          id="field_of_study"
          value={formData.field_of_study}
          onChange={(e) => {
            setFormData({ ...formData, field_of_study: e.target.value });
            setErrors(prev => ({ ...prev, field_of_study: undefined }));
          }}
          className={cn(errors.field_of_study && "border-red-500")}
        />
        {errors.field_of_study && (
          <p className="text-sm text-red-500 mt-1">{errors.field_of_study}</p>
        )}
      </div>

      <div>
        <Label htmlFor="start_year">
          Start Year <span className="text-red-500">*</span>
        </Label>
        <Input
          id="start_year"
          type="number"
          min="1900"
          max="2100"
          value={formData.start_year}
          onChange={(e) => {
            setFormData({ ...formData, start_year: e.target.value });
            setErrors(prev => ({ ...prev, start_year: undefined }));
          }}
          className={cn(errors.start_year && "border-red-500")}
        />
        {errors.start_year && (
          <p className="text-sm text-red-500 mt-1">{errors.start_year}</p>
        )}
      </div>

      <div>
        <Label htmlFor="end_year">End Year (leave empty if ongoing)</Label>
        <Input
          id="end_year"
          type="number"
          min="1900"
          max="2100"
          value={formData.end_year}
          onChange={(e) =>
            setFormData({ ...formData, end_year: e.target.value })
          }
        />
      </div>

      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">Save</Button>
      </div>
    </form>
  );
};

export default EducationForm;